/* @flow */
import PromptStore from 'stores/PromptStore';
import GroupStore from 'groups/stores/GroupStore';
import GroupActions from 'groups/actions/GroupActions';
import { PromptNames } from 'constants/PromptConstants';

function needsSchool(user) {
  const groupsJoined = GroupStore.getGroupsJoinedDuringSetup();
  const joinedGroupWithSchool = groupsJoined && groupsJoined.some(group => group.getOrganization());

  return user.getNeeds().organization && !joinedGroupWithSchool;
}

function needsGroup(user) {
  return (
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_CLASS) &&
    user.needsGroups() && // User object isn't updated when a group is created
    GroupStore.getOwnedGroups().length === 0
  );
}

function needsSignature(user) {
  return (
    user.getNeeds().signature &&
    !user.getSignature() &&
    user.getSignInCount() < 3 &&
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_SIGNATURE)
  );
}

function needsSmsDevice(user) {
  return (
    user.getSignInCount() <= 2 &&
    user.hasSmsSupported() &&
    // The user isn't updated after this setup step completes, but it works anyway because of the prompt. If removing the prompt, you'll need to retetch the user after they finish this setup step.
    !user.hasSmsDevice() &&
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_SMS_DEVICE)
  );
}

function needsSubscription(user) {
  return (
    user.getSignInCount() <= 2 &&
    user.getNeeds().subscriptions &&
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_FIRST_SUBSCRIPTION)
  );
}

function needsFullName(user) {
  return user.getNeeds().full_name;
}

function needsEmail(user) {
  return !user.hasEmail();
}

function needRole(user) {
  return user.getNeeds().role;
}

function needsPassword(user) {
  return user.getNeeds().password;
}

function needPIIConsent(user) {
  return user.getNeeds().pii_consent;
}

// student always need a birthdate (new 2018 ikeepsafe requirement)
function needStudentBirthdate(user) {
  return user.getSignInCount() <= 2 && !user.getBirthdate();
}

function needsRosterAll(user) {
  return user.getNeeds().roster_all;
}

function needsRosterChoose(user) {
  return user.getNeeds().roster_choose;
}

function needsRosterConfirm(user) {
  return user.getNeeds().roster_confirm;
}

function clearSetupData() {
  // Prevents "Can't dispatch within a dispatch" error when booting the app. The other dispatch is loading groups.
  setTimeout(() => {
    GroupActions.clearGroupsJoinedDuringSetup();
  }, 0);
}

type Steps = { if: (user: *) => boolean, step: string };

// prettier-ignore
const prerequisites: Array<Steps> = [
  { if: needPIIConsent, step: '/setup/pii'  },
  { if: needRole,       step: '/setup/role' },
];

// prettier-ignore
const steps: { [string]: Array<Steps> } = {
  teacher: [
    { if: needsPassword,      step: '/setup/password'       },
    { if: needsFullName,      step: '/setup/name'           },
    { if: needsSignature,     step: '/setup/signature'      },
    { if: needsEmail,         step: '/setup/email'          },
    { if: needsGroup,         step: '/setup/class'          },
    { if: needsSchool,        step: '/setup/school'         },
    { if: needsRosterConfirm, step: '/setup/roster/confirm' },
    { if: needsRosterAll,     step: '/setup/roster/all'     },
    { if: needsRosterChoose,  step: '/setup/roster/choose'  },
  ],
  student: [
    { if: needsPassword,        step: '/setup/password'       },
    { if: needStudentBirthdate, step: '/setup/birthdate'      },
    { if: needsSmsDevice,       step: '/setup/device/sms'     },
    { if: needsSubscription,    step: '/setup/subscriptions'  },
    { if: needsFullName,        step: '/setup/name'           },
    { if: needsRosterConfirm,   step: '/setup/roster/confirm' },
    { if: needsRosterAll,       step: '/setup/roster/all'     },
    { if: needsRosterChoose,    step: '/setup/roster/choose'  },
  ],
  parent: [
    { if: needsPassword,      step: '/setup/password'       },
    { if: needsFullName,      step: '/setup/name'           },
    { if: needsSmsDevice,     step: '/setup/device/sms'     },
    { if: needsSubscription,  step: '/setup/subscriptions'  },
    { if: needsRosterConfirm, step: '/setup/roster/confirm' },
    { if: needsRosterAll,     step: '/setup/roster/all'     },
    { if: needsRosterChoose,  step: '/setup/roster/choose'  },
  ],
};

function getStepsForRole(role: ?string): Array<Steps> {
  return [...prerequisites, ...(role ? steps[role] || [] : [])];
}

export default {
  getNextEligibleStep: (user: *) => {
    const stepsForRole = getStepsForRole(user.getRole());
    const eligibleStep = stepsForRole.find(step => step.if(user));

    if (eligibleStep) {
      return eligibleStep.step;
    }

    clearSetupData();
    return undefined;
  },
};
